/**
 * 通知策略。**这个模块同时被页面和 Service Worker import。**
 *
 * 这一点是整个框架里最重要的结构决策。以前 SW 不是模块、import 不了常量，于是通知 tag
 * 规则、目标路径推导在两处各写一份，改一边漏一边——而漏掉的那次完全静默，只有真机点
 * 通知才发现。vite-plugin-pwa 的 injectManifest 会打包 SW，所以现在这些规则**只存在一份**。
 *
 * 因此本文件必须保持纯：不碰 DOM，不碰 self，不碰 window。类型全部用结构化定义，
 * 不引用 DOM 的 Notification / NotificationOptions（那两个类型在 WebWorker lib 里长得不一样）。
 *
 * 三条来自生产事故的规则：
 *
 * 1. **角标和通知是两套互不相干的东西**。清角标不会动通知中心里那条横幅。用户读完消息
 *    看到角标没了、横幅还在，会以为还有没看的，点进来什么都没有。
 * 2. **同 tag 应该替换，但 iOS 会堆叠**。规范说同 tag 的新通知替换旧的，iOS 的 PWA 通知
 *    中心实测把每一次 renotify 都留成一条新记录。对策是弹新的之前先显式关掉同 tag 的旧的。
 * 3. **来电类通知必须独立 tag，且不能被"顺手已读"关掉**。被一条新消息静默替换掉 =
 *    用户永远不知道有人打过来。
 */
/** 通知负载。服务端发什么、SW 收到什么、页面据此关哪些通知，全用这一个形状。 */
import { sanitizePath } from './path.js';
export const DEFAULTS = {
    fallbackTitle: '新通知',
    defaultCollapseKey: 'default',
};
/**
 * tag 的划分决定了会不会漏接电话。
 *
 * - 持久通知（来电）：**每条一个独立 tag**，谁都替换不掉它。
 * - 其它：共用一个折叠键，通知中心里始终只有一条。
 */
export function notificationTag(payload, defaults = DEFAULTS) {
    if (payload.persistent) {
        const id = payload.collapseKey ?? payload.threadId ?? 'default';
        return `persistent:${id}`;
    }
    return payload.collapseKey ?? defaults.defaultCollapseKey ?? 'default';
}
/**
 * 把推送负载解析成安全的形状。
 *
 * 服务端改字段、老客户端收到新负载、负载压根不是 JSON —— 这些都不该导致"通知没弹出来"。
 * 宁可弹一条兜底文案，也不要静默丢掉。
 */
export function normalizePayload(raw, defaults = DEFAULTS) {
    if (typeof raw !== 'object' || raw === null) {
        return { title: defaults.fallbackTitle };
    }
    const o = raw;
    // Declarative Web Push 的形状：{ web_push: 8030, notification: {...} }
    const declarative = typeof o['notification'] === 'object' && o['notification'] !== null ? o['notification'] : null;
    const src = declarative ?? o;
    const data = (typeof src['data'] === 'object' && src['data'] !== null ? src['data'] : o['data']);
    const title = typeof src['title'] === 'string' && src['title'].trim() ? src['title'] : defaults.fallbackTitle;
    const out = { title };
    const str = (v) => (typeof v === 'string' && v ? v : undefined);
    const body = str(src['body']);
    if (body !== undefined)
        out.body = body;
    // declarative 里点击目标叫 navigate，我们内部统一叫 target
    const target = str(src['target']) ?? str(src['navigate']) ?? str(data?.['target']);
    if (target !== undefined)
        out.target = target;
    const collapseKey = str(src['collapseKey']) ?? str(src['tag']) ?? str(data?.['collapseKey']);
    if (collapseKey !== undefined)
        out.collapseKey = collapseKey;
    const threadId = str(src['threadId']) ?? str(data?.['threadId']);
    if (threadId !== undefined)
        out.threadId = threadId;
    const persistent = src['persistent'] ?? data?.['persistent'];
    if (persistent === true)
        out.persistent = true;
    const badge = src['badge'] ?? o['badge'] ?? data?.['badge'];
    if (typeof badge === 'number' && Number.isFinite(badge))
        out.badge = Math.max(0, Math.trunc(badge));
    const icon = str(src['icon']);
    if (icon !== undefined)
        out.icon = icon;
    if (src['silent'] === true)
        out.silent = true;
    if (data)
        out.data = data;
    return out;
}
export function buildNotification(payload, defaults = DEFAULTS) {
    const tag = notificationTag(payload, defaults);
    // ⚠️ 顺序不能反：业务自带的 data 先铺，派生字段后写。
    // 反过来的话，一条 `{ persistent: true, data: { persistent: false } }`
    // （或者服务端习惯性把业务字段也镜像进 data）就能把 persistent 覆盖掉，
    // 于是 notificationsToClose 不再豁免它——来电通知被下一次 markRead 关掉，
    // 而 persistent 这套设计存在的唯一理由就是防这件事。
    const data = { payload };
    if (payload.data)
        Object.assign(data, payload.data);
    if (payload.threadId !== undefined)
        data.threadId = payload.threadId;
    if (payload.persistent !== undefined)
        data.persistent = payload.persistent;
    if (payload.target !== undefined)
        data.target = payload.target;
    const options = {
        body: payload.body ?? '',
        tag,
        // 同 tag 覆盖时**默认是静默替换**，不弹横幅、不响。必须显式 renotify 才会再次提醒。
        // （规范要求 renotify 必须配 tag，上面保证了 tag 一定非空。）
        renotify: true,
        data,
    };
    const icon = payload.icon ?? defaults.icon;
    if (icon)
        options.icon = icon;
    const badgeIcon = payload.badgeIcon ?? defaults.badgeIcon;
    if (badgeIcon)
        options.badge = badgeIcon;
    if (payload.silent)
        options.silent = true;
    return { title: payload.title || defaults.fallbackTitle, options };
}
/**
 * 读完消息后该关掉哪些已弹出的通知。
 *
 * 起因：角标在 markRead 里清零，横幅却一直留在通知中心——用户以为还有没看的。
 *
 * 判定顺序（每一条都对应一个踩过的坑）：
 * 1. **持久通知一律留着**：正在响的来电不能被"顺手读了条消息"关掉。
 * 2. **全读完 → 全关**：这是唯一能保证不留残留的时机。
 * 3. **还有别的没读 → 只关刚读的那个线程**：别把仅剩的提醒抹掉。
 * 4. **认不出线程的通知留着**：宁可多留一条，不可抹掉用户唯一的提醒。
 *
 * 纯函数是为了能测；真正调 close() 的地方在调用侧（要拿 SW registration）。
 */
export function notificationsToClose(notifications, ctx) {
    const readThreads = new Set((ctx.readThreadIds ?? []).map(String));
    return notifications.filter((n) => {
        const data = (typeof n.data === 'object' && n.data !== null ? n.data : {});
        if (data.persistent === true)
            return false;
        if (ctx.unreadTotal <= 0)
            return true;
        if (readThreads.size === 0)
            return false;
        const threadId = data.threadId;
        if (threadId === undefined)
            return false;
        return readThreads.has(String(threadId));
    });
}
/** 点击通知后该去的应用内路径。 */
export function notificationTarget(data, fallback = '/') {
    // ⚠️ 这个值**来自网络负载**，一路会流到 `clients.openWindow()` 和应用路由。
    // 不净化的话，一条 `{"navigate":"https://evil.test"}` 就能让用户点通知直接出站。
    // 净化失败一律回落到 fallback——点了通知落到首页是遗憾，落到钓鱼页是事故。
    if (typeof data === 'object' && data !== null) {
        const d = data;
        if (typeof d.target === 'string' && d.target)
            return sanitizePath(d.target, fallback);
        if (d.payload && typeof d.payload.target === 'string' && d.payload.target) {
            return sanitizePath(d.payload.target, fallback);
        }
    }
    return fallback;
}
//# sourceMappingURL=notifications.js.map